import { readItem, readItems } from "@directus/sdk";

import Combobox from "components/combobox";
import React from "react";
import { object } from "zod";

function defaultMapItem(item, valueKey = "id") {
  const value = item?.[valueKey];
  const label = item?.name ?? String(value ?? "");
  return { value, label, select: label };
}

export default function DirectusCombobox({
  client,
  collection,
  value: initialValue,
  onChange,
  className,
  label,
  // Data/format overrides
  fields: fieldsOverride,
  searchKeys: searchKeysOverride,
  mapItem,
  valueKey = "id",
  returnItem = false,
  filter: baseFilter,
  createSearchFilter,
  clearable,
  autoSelectFirst = false,
  // Directus query controls
  limit = 10,
  sort,
  page,
  offset,
  meta,
  deep,
  query = {},

  listRequest, // optional: (q) => request
  itemRequest, // optional: (id, q) => request
  // UI
  placeholder = "Search & select",
  searchPlaceholder = "Search...",
  disabled = false,
}) {
  const [search, setSearch] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [value, setValue] = React.useState(initialValue);

  const collectionKey = String(collection || "");
  const listReq = listRequest || ((q) => readItems(collectionKey, q));
  const itemReq = itemRequest || ((id, q) => readItem(collectionKey, id, q));
  const fields = fieldsOverride || ["id", "name"];
  const searchKeys = searchKeysOverride || ["name"];

  React.useEffect(() => {
    let ignore = false;
    if (disabled) return;
    async function run() {
      setLoading(true);
      try {
        // Build search filter (simple default; override via createSearchFilter)
        const buildDefaultSearchFilter = (term, keys) => {
          if (!term) return undefined;
          const terms = String(term).trim().split(" ").filter(Boolean);
          if (!terms.length) return undefined;
          return {
            _or: keys
              .filter((k) => k !== "id")
              .map((k) => ({ [k]: { _icontains: terms.join(" ") } })),
          };
        };

        const searchFilter = search
          ? createSearchFilter?.(search, { fields, searchKeys }) ||
            buildDefaultSearchFilter(search, searchKeys)
          : undefined;

        const finalFilter =
          baseFilter && searchFilter
            ? { _and: [baseFilter, searchFilter] }
            : baseFilter || searchFilter || undefined;

        const listQuery = {
          ...query,
          fields,
          ...(finalFilter ? { filter: finalFilter } : {}),
          ...(typeof limit === "number" ? { limit } : {}),
          ...(sort ? { sort } : {}),
          ...(typeof page === "number" ? { page } : {}),
          ...(typeof offset === "number" ? { offset } : {}),
          ...(meta ? { meta } : {}),
          ...(deep ? { deep } : {}),
        };

        const list = await client.request(listReq(listQuery));
        if (!ignore) setItems(Array.isArray(list) ? list : []);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (client && collectionKey) run();
    return () => {
      ignore = true;
    };
  }, [
    client,
    collectionKey,
    search,
    JSON.stringify(baseFilter),
    JSON.stringify(fields),
    JSON.stringify(searchKeys),
    createSearchFilter,
    limit,
    JSON.stringify(sort),
    page,
    offset,
    JSON.stringify(meta),
    JSON.stringify(deep),
    JSON.stringify(query),
    disabled,
  ]);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (newValue) => {
    setValue(newValue);
    onChange?.(newValue);
  };

  const data = React.useMemo(
    () =>
      items.map((it) => (mapItem ? mapItem(it) : defaultMapItem(it, valueKey))),
    [items, mapItem, valueKey],
  );

  // Auto select the first item when requested and no value is set yet
  React.useEffect(() => {
    if (!autoSelectFirst || disabled) return;
    // Treat empty string or null/undefined as no selection
    const hasValue = value !== undefined && value !== null && value !== "";
    if (!hasValue && data.length > 0) {
      const first = data[0];
      setValue(first.value);
      onChange?.(first.value);
    }
  }, [autoSelectFirst, disabled, data, value, onChange]);

  const fetchSelected = React.useCallback(
    async (val) => {
      if (!value) return null;
      const item = await client.request(itemReq(val, { fields }));
      if (!item) return null;
      return mapItem ? mapItem(item) : defaultMapItem(item, valueKey);
    },
    [client, itemReq, fields, mapItem, valueKey],
  );

  return (
    <Combobox
      data={data}
      value={value}
      onChange={handleChange}
      returnItem={returnItem}
      searchable
      search={search}
      onSearch={setSearch}
      isLoading={loading}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      fetchSelected={fetchSelected}
      disabled={disabled}
      className={className}
      label={label}
      clearable={clearable}
    />
  );
}
